/**
 * 新车队报告
 */
import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Button,
  Descriptions,
  InputNumber,
  Select,
  Input,
  TableProps,
  message,
} from "antd";
import dayjs from "dayjs";
import * as echarts from "echarts/core";
import DatePicker from "@/components/datePicker";
import LoadingWrapper from "@/components/loadingWrapper";
import CommonTable from "@/components/commonTable";
import ExportButton from "@/components/exportButton";
import RenderEllipsisColumn from "@/components/renderEllipsisColumn";
import {
  fleetRiskReportRequest,
  motorcadeCarList,
  fleetRiskReportExportRequestPath,
  cateAllCompanyRequest,
} from "@/service/fleetRisk";
import { limitDayRange } from "@/utils/operateSearchParams";
import styles from "./index.module.scss";
import { companyListRequest } from "@/service/companyManage";
import PermissionButton from "@/components/permissionButton";
import { downloadUrl } from "@/utils/download";
import {
  carListRequest,
  CarListRequestParams,
  carReportPath,
  reportListRequest,
  createReportPathRequest,
  CompanyWeekReportListDto,
} from "@/service/carManage";
import { CarRiskScoreRange } from "@/service/carRisk";
import { getInnerPermissionEnable } from "@/utils/permission";

const levelOptions = [
  { value: "高风险", label: "高风险" },
  { value: "中风险", label: "中风险" },
  { value: "低风险", label: "低风险" },
];

/** 业务模式选项（与车辆报告保持一致） */
const MODE_OPTIONS = [
  { label: "中富模式", value: "中富模式" },
  { label: "保险模式", value: "保险模式" },
];

/** 评分/风险百分比区间选项（业务展示值 -> 接口传值）
 * 后端接口为左开右闭 (start, end]，业务需求：
 * - 1-10 展示为 1-10，实际传值 start=-1, end=10
 * - 其余区间展示值与传值一致
 */
const RANGE_OPTIONS: { label: string; value: CarRiskScoreRange }[] = [
  { label: "1-10", value: { start: -1, end: 10 } },
  { label: "10-20", value: { start: 10, end: 20 } },
  { label: "20-30", value: { start: 20, end: 30 } },
  { label: "30-40", value: { start: 30, end: 40 } },
  { label: "40-50", value: { start: 40, end: 50 } },
  { label: "50-60", value: { start: 50, end: 60 } },
  { label: "60-70", value: { start: 60, end: 70 } },
  { label: "70-80", value: { start: 70, end: 80 } },
  { label: "80-90", value: { start: 80, end: 90 } },
  { label: "90-100", value: { start: 90, end: 100 } },
];

/** 生成带后缀的区间选项 */
const getRangeOptions = (suffix: string) =>
  RANGE_OPTIONS.map((item) => ({
    label: `${item.label}${suffix}`,
    value: JSON.stringify(item.value),
  }));

const CarReprot = () => {
  const [carData, setCarData] = useState<CompanyWeekReportListDto[]>([]); // 车辆数据
  const [remarkContent, setRemarkContent] = useState<any>(); // 备注
  const [loading, setLoading] = useState(false); // 是否在获取报告中
  const [allCompany, setAllCompany] = useState<any>([]); // 车队选择
  const [totalCount, setTotalCount] = useState(0); // 车辆总数
  const [pageNum, setPageNum] = useState(1); // 页码
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(40); // 每一页显示的数量
  const [serachParams, setSerachParams] = useState<Record<string, any>>({
    cate_id: [],
  }); // 搜索相关的参数
  const [downloadList, setDownloadList] = useState<any[]>([]);

  useEffect(() => {
    getCateAllCompanyData();
    form.setFieldValue("reportDate", dayjs().day(-7).startOf("week"));
  }, []);

  const getCateAllCompanyData = async () => {
    // const res = await companyListRequest({page: 1, limit: 20000, name: ''});
    const res = await cateAllCompanyRequest();
    const data = res.data?.data || [];
    //循环遍历data
    const aa: any = [];
    data.forEach((item: any) => {
      // aa.push({label: item.name, value: item.name})
      aa.push({ label: item.title, value: item.title });
    });
    setAllCompany(aa);
  };

  // 将表单值转换为接口查询参数
  const buildSearchParams = (values: any, isExport?: boolean): Record<string, any> => {
    const params: Record<string, any> = {};

    if (values.reportDate) {
      params.reportDate = dayjs(values.reportDate).format("YYYY-MM-DD");
    }

    if (values.name) {
      params.name = values.name;
    }
    if (values.grade) {
      params.grade = values.grade;
    }
    if (values.mode) {
      params.mode = values.mode;
    }

    if (values.scoreRange && Array.isArray(values.scoreRange)) {
      values.scoreRange.forEach((rangeStr: string, index: number) => {
        try {
          const range = JSON.parse(rangeStr) as CarRiskScoreRange;
          params[`scoreRanges[${index}].start`] = range.start;
          params[`scoreRanges[${index}].end`] = range.end;
        } catch (error) {
          message.error("车队评分参数格式错误");
        }
      });
    }

    if (values.riskPercentRange && Array.isArray(values.riskPercentRange)) {
      values.riskPercentRange.forEach((rangeStr: string, index: number) => {
        try {
          const range = JSON.parse(rangeStr) as CarRiskScoreRange;
          params[`riskPercentRanges[${index}].start`] = range.start;
          params[`riskPercentRanges[${index}].end`] = range.end;
        } catch (error) {
          message.error("风险百分比参数格式错误");
        }
      });
    }

    if (isExport) {
      params.export = true;
    }

    return params;
  };

  // 搜索
  const onSearch = async (_values?: any) => {
    const values = form.getFieldsValue();
    const newSearchParams = buildSearchParams(values);
    console.log(newSearchParams);
    setSerachParams(newSearchParams);
    getCarData({
      page: 1,
      limit: pageSize,
      ...newSearchParams,
    });
  };

  // 获取导出的参数
  const getExportParams = () => {
    const values = form.getFieldsValue();
    return buildSearchParams(values, true);
  };

  // 获取车辆数据
  const getCarData = async (params: Record<string, any>) => {
    try {
      setCarData([]);
      setLoading(true);
      const res = await reportListRequest(params);

      const resData = res.data?.data || [];
      const resDataWithIdxKey = resData.map((item: any, index: number) => {
        return {
          ...item,
          key: item.id != null ? `${item.id}-${index}` : index,
        };
      });
      setCarData(resDataWithIdxKey);
      setTotalCount(res.data?.count || 0);
      setPageNum(params.page);
      setLoading(false);
    } catch (error) {
      console.warn("error", error);
      setLoading(false);
    }
  };
  const creatReport = (id: any) => {
    createReportPathRequest({id}).then(() => {
      getCarData({
        page: pageNum,
        limit: pageSize,
        ...serachParams,
      });
    })
  }

  // 表格列显示
  const columns = [
    { title: "日期", dataIndex: "dateRange", width: 180, render: (v: string | null) => v ?? "--" },
    { title: "车队等级", dataIndex: "grade", width: 110 },
    { title: "车队名称", dataIndex: "name", width: 150 },
    { title: "车队评分", dataIndex: "score", width: 110 },
    { title: "业务模式", dataIndex: "mode", width: 120 },
    { title: "车辆总数", dataIndex: "totalCount", width: 110 },
    { title: "运行车辆数", dataIndex: "runningCount", width: 120 },
    { title: "风险车辆数", dataIndex: "riskCount", width: 120 },
    { title: "风险百分比", dataIndex: "riskPercent", width: 120, render: (v: number | null) => (v != null ? `${v}%` : "--") },
    {
      title: "操作",
      dataIndex: "company",
      width: 110,
      render: (value: any, item: any) => {
        return (
          <>
            {item.url && (
              <PermissionButton
                operateType="/car-admin/report/report-edit"
                type="link"
                onClick={() =>
                  downloadUrl(
                    item.url,
                    `${item.name}_${item.week}_车队报告.ppt`
                  )
                }
              >
                下载
              </PermissionButton>
            )}
            {(!item.url && (item.state === null || item.state === 1)) && (
              <PermissionButton
                operateType="/car-admin/report/report-edit"
                type="link"
                onClick={() =>{creatReport(item.id)}
                }
              >
                生成报告
              </PermissionButton>
            )}
            {!item.url && item.state === 0 && (
              <span>生成中</span>
            )}
          </>
        );
      },
      fixed: "right" as "right",
    },
  ];

  // 翻页器信息改变
  const onPageChange = (page: number, newPageSize: number) => {
    const newPageNum = newPageSize !== pageSize ? 1 : page;
    setPageNum(newPageNum);
    setPageSize(newPageSize);
    getCarData({
      page: newPageNum,
      limit: newPageSize,
      ...serachParams,
    });
  };

  const rowSelection: TableProps<any>["rowSelection"] = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows
      );
      setDownloadList(selectedRows);
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.url === null, // Column configuration not to be checked
      name: record.name,
    }),
  };

  const downloadFileAllFn = () => {
    if (!downloadList.length) {
      message.error("请先选择数据");
    } else {
      console.log(downloadList, 'downloadList')
      downloadList.forEach((item, index) => {
        setTimeout(() => {
          downloadUrl(item.url, `${item.name}_${item.week}_车队报告.ppt`);
        }, 1000 * index)
      });
    }
  };

  useEffect(() => {
    onSearch({})
  }, [])

  return (
    <Card bordered={false} className={styles.fleetRisk}>
      <Form
        onFinish={onSearch}
        form={form}
        autoComplete="off"
        layout="vertical"
      >
        <Row gutter={24} wrap>
          <Col span={4}>
            <Form.Item label="报告周期" name="reportDate">
              <DatePicker
                allowClear
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="车队选择"
              name="name"
            >
              <Select
                showSearch
                placeholder="请选择"
                style={{ width: "100%" }}
                options={allCompany}
                allowClear
              />
            </Form.Item>
          </Col>
          {getInnerPermissionEnable('/car-admin/report/level') &&  <Col span={4}>
            <Form.Item label="评级" name="grade">
              <Select
                placeholder="请选择"
                style={{ width: "100%" }}
                options={levelOptions}
                allowClear
              />
            </Form.Item>
          </Col>}
          <Col span={4}>
            <Form.Item label="业务模式" name="mode">
              <Select
                placeholder="请选择"
                style={{ width: "100%" }}
                options={MODE_OPTIONS}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="车队评分" name="scoreRange">
              <Select
                placeholder="请选择"
                style={{ width: "100%" }}
                options={getRangeOptions("分")}
                allowClear
                mode="multiple"
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="风险百分比" name="riskPercentRange">
              <Select
                placeholder="请选择"
                style={{ width: "100%" }}
                options={getRangeOptions("%")}
                allowClear
                mode="multiple"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item shouldUpdate>
              {() => (
                <div className={styles.searchBox}>
                  <Button type="primary" htmlType="submit">
                    搜索
                  </Button>
                  <ExportButton
                    url={carReportPath}
                    fileName={`${form.getFieldValue("name")}车队报告`}
                    getParamsFunc={getExportParams}
                    requestMethod="GET"
                    permissionKey="/car-admin/fleet-risk/export"
                  >
                    导出页面数据
                  </ExportButton>
                  <Button onClick={downloadFileAllFn}>批量下载报告</Button>
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <LoadingWrapper spinning={loading}>
        <CommonTable
          rowSelection={{ type: "checkbox", ...rowSelection }}
          rowKey="key"
          columns={columns}
          dataSource={carData}
          pagination={{
            total: totalCount,
            current: pageNum,
            pageSize,
            onChange: onPageChange,
          }}
          bordered
          loading={loading}
          // tableInScreen={false}
          scroll={{ x: "100%", y: "100%" }}
        />
      </LoadingWrapper>
    </Card>
  );
};

export default CarReprot;
